/**
 * Interim editor landing page for build 1: every request in one filterable
 * list so imported items can be reviewed and scored. Replaced by the approved
 * Home ("My day" / "Company snapshot") in build 2.
 */
import { useMemo, useState } from 'react';
import { DIVISIONS, STATUSES, TYPES, score } from '../config';
import { ageLabel, type Req } from '../lib/data';
import { Divisions, Empty, StatusTag, TierBadge, TypeTag } from '../ui';
import type { Settings } from '../config';

export function AllRequests({ items, settings, onOpen }: { items: Req[]; settings: Settings; onOpen: (id: string) => void }) {
  const [q, setQ] = useState('');
  const [div, setDiv] = useState('');
  const [status, setStatus] = useState('open');
  const [type, setType] = useState('');

  const rows = useMemo(() => items
    .filter((r) => status === 'open' ? r.status !== 'completed' : !status || r.status === status)
    .filter((r) => !div || r.divisions.includes(div as never))
    .filter((r) => !type || r.type === type)
    .filter((r) => !q || `${r.title} ${r.requesterName} ${r.description ?? ''}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (score(b.impact, b.urgency) ?? -1) - (score(a.impact, a.urgency) ?? -1)),
  [items, q, div, status, type]);

  const unscored = items.filter((r) => r.status !== 'completed' && score(r.impact, r.urgency) == null).length;

  return (
    <div className="card">
      <div className="toolbar">
        <h2>All requests <span className="count">{rows.length}</span></h2>
        {unscored > 0 && <span className="hint">{unscored} not scored yet</span>}
        <span className="spacer" />
        <input className="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="open">Open</option>
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={div} onChange={(e) => setDiv(e.target.value)}>
          <option value="">All divisions</option>
          {DIVISIONS.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </div>
      {!rows.length && <Empty>No requests match.</Empty>}
      <table className="list">
        <thead><tr><th>Request</th><th>Asked by</th><th>Divisions</th><th>Type</th><th>Score</th><th>Status</th><th>Age</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r.id)}>
              <td className="t">{r.title}{r.waitingOn && <span className="waiting"> waiting: {r.waitingOn}</span>}</td>
              <td>{r.requesterName}</td>
              <td><Divisions ids={r.divisions} /></td>
              <td><TypeTag type={r.type} /></td>
              <td><TierBadge r={r} settings={settings} /></td>
              <td><StatusTag status={r.status} /></td>
              <td className="num">{ageLabel(r.createdAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
